import { Option } from "@swan-io/boxed";
import { Box } from "@swan-io/lake/src/components/Box";
import { Cell, CopyableTextCell, HeaderCell, TextCell } from "@swan-io/lake/src/components/Cells";
import { EmptyView } from "@swan-io/lake/src/components/EmptyView";
import { LakeAlert } from "@swan-io/lake/src/components/LakeAlert";
import { LakeButton, LakeButtonGroup } from "@swan-io/lake/src/components/LakeButton";
import { LakeCheckbox } from "@swan-io/lake/src/components/LakeCheckbox";
import { LakeText } from "@swan-io/lake/src/components/LakeText";
import { Popover } from "@swan-io/lake/src/components/Popover";
import { ResponsiveContainer } from "@swan-io/lake/src/components/ResponsiveContainer";
import { Space } from "@swan-io/lake/src/components/Space";
import { Tag } from "@swan-io/lake/src/components/Tag";
import { Tile } from "@swan-io/lake/src/components/Tile";
import { ColumnConfig, VirtualizedList } from "@swan-io/lake/src/components/VirtualizedList";
import { colors, negativeSpacings, spacings } from "@swan-io/lake/src/constants/design";
import { printFormat } from "iban";
import { useRef, useState } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { match } from "ts-pattern";
import { CreditTransferInput, VerifyBeneficiarySuccessPayloadFragment } from "../graphql/partner";
import { formatCurrency, formatNestedMessage, t } from "../utils/i18n";

const styles = StyleSheet.create({
  scrollViewContainer: {
    marginHorizontal: negativeSpacings[32],
    flexGrow: 1,
    flexShrink: 1,
  },
  name: {
    flexDirection: "row",
    alignItems: "center",
    userSelect: "none",
    flexGrow: 1,
    flexShrink: 1,
    paddingHorizontal: spacings[16],
  },
  buttonMini: {
    height: 32,
    width: 32,
    minWidth: 32,
  },
});

type Props = {
  creditTransferInputs: (CreditTransferInput & {
    beneficiaryVerification?: VerifyBeneficiarySuccessPayloadFragment;
  })[];
  initialSelectedCreditTransferInputs?: (CreditTransferInput & {
    beneficiaryVerification?: VerifyBeneficiarySuccessPayloadFragment;
  })[];
  onPressPrevious: () => void;
  onSave: (
    creditTransfers: (CreditTransferInput & {
      beneficiaryVerification?: VerifyBeneficiarySuccessPayloadFragment;
    })[],
  ) => void;
};

type ExtraInfo = {
  selected: Set<number>;
  setSelected: (set: Set<number>) => void;
  totalCount: number;
};

const stickedToStartColumns: ColumnConfig<CreditTransferInput, ExtraInfo>[] = [
  {
    id: "name",
    title: t("transfer.bulk.beneficiaryName"),
    width: 250,
    renderTitle: ({ title, extraInfo: { selected, setSelected, totalCount } }) => {
      const checked = selected.size === totalCount ? true : selected.size === 0 ? false : "mixed";
      return (
        <Pressable
          style={styles.name}
          onPress={() => {
            setSelected(
              selected.size > 0
                ? new Set()
                : new Set(Array.from({ length: totalCount }, (_, index) => index)),
            );
          }}
          role="checkbox"
          aria-checked={checked}
        >
          <LakeCheckbox value={checked} />
          <Space width={8} />

          <LakeText variant="semibold" color={colors.gray[900]} numberOfLines={1}>
            {title}
          </LakeText>
        </Pressable>
      );
    },
    renderCell: ({ item, index, extraInfo: { selected, setSelected } }) => (
      <Pressable
        style={styles.name}
        onPress={() => {
          const nextSelected = new Set([...selected]);
          if (nextSelected.has(index)) {
            nextSelected.delete(index);
          } else {
            nextSelected.add(index);
          }
          setSelected(nextSelected);
        }}
        role="checkbox"
        aria-checked={selected.has(index)}
      >
        <LakeCheckbox value={selected.has(index)} />
        <Space width={8} />

        <LakeText variant="semibold" color={colors.gray[900]} numberOfLines={1}>
          {item.sepaBeneficiary?.name}
        </LakeText>
      </Pressable>
    ),
  },
];

const columns: ColumnConfig<CreditTransferInput, ExtraInfo>[] = [
  {
    id: "label",
    title: t("transfer.bulk.label"),
    width: 150,
    renderTitle: ({ title }) => <HeaderCell text={title} />,
    renderCell: ({ item }) => {
      const label = item.label;
      return <TextCell color={colors.gray[700]} text={label ?? "-"} />;
    },
  },
  {
    id: "iban",
    title: t("transfer.bulk.iban"),
    width: 340,
    renderTitle: ({ title }) => <HeaderCell text={title} />,
    renderCell: ({ item }) => {
      const iban = item.sepaBeneficiary?.iban;
      if (iban == null) {
        return null;
      }
      return (
        <CopyableTextCell
          variant="regular"
          text={printFormat(iban)}
          textToCopy={printFormat(iban)}
          copyWording={t("copyButton.copyTooltip")}
          copiedWording={t("copyButton.copiedTooltip")}
        />
      );
    },
  },
  {
    id: "reference",
    title: t("transfer.bulk.reference"),
    width: 150,
    renderTitle: ({ title }) => <HeaderCell text={title} />,
    renderCell: ({ item }) => {
      const reference = item.reference;
      return <TextCell color={colors.gray[700]} text={reference ?? "-"} />;
    },
  },
];

const VerificationCell = ({
  beneficiaryVerification,
}: {
  beneficiaryVerification: VerifyBeneficiarySuccessPayloadFragment | undefined;
}) => {
  const buttonRef = useRef<View>(null);
  const [layer, setLayer] = useState<
    Option<VerifyBeneficiarySuccessPayloadFragment["verifyBeneficiaryResult"]>
  >(Option.None());
  return (
    <Cell align="right" direction="row">
      {match(beneficiaryVerification?.verifyBeneficiaryResult)
        .with({ __typename: "VerifyBeneficiaryMatch" }, () => (
          <>
            <Tag color="positive">{t("transfer.bulk.beneficiaryVerification.match")}</Tag>
            <Space width={4} />
            <Space width={32} />
          </>
        ))
        .with({ __typename: "VerifyBeneficiaryCloseMatch" }, value => (
          <>
            <Tag color="shakespear">{t("transfer.bulk.beneficiaryVerification.closeMatch")}</Tag>

            <Space width={4} />

            <LakeButton
              ref={buttonRef}
              size="small"
              style={styles.buttonMini}
              mode="tertiary"
              icon="info-regular"
              onPress={() => setLayer(Option.Some(value))}
              ariaLabel={t("common.showMore")}
            />
          </>
        ))
        .with({ __typename: "VerifyBeneficiaryNotPossible" }, value => (
          <>
            <Tag color="warning">{t("transfer.bulk.beneficiaryVerification.notPossible")}</Tag>

            <Space width={4} />

            <LakeButton
              ref={buttonRef}
              size="small"
              style={styles.buttonMini}
              mode="tertiary"
              icon="info-regular"
              onPress={() => setLayer(Option.Some(value))}
              ariaLabel={t("common.showMore")}
            />
          </>
        ))
        .with({ __typename: "VerifyBeneficiaryNoMatch" }, value => (
          <>
            <Tag color="negative">{t("transfer.bulk.beneficiaryVerification.noMatch")}</Tag>

            <Space width={4} />

            <LakeButton
              ref={buttonRef}
              size="small"
              style={styles.buttonMini}
              mode="tertiary"
              icon="info-regular"
              onPress={() => setLayer(Option.Some(value))}
              ariaLabel={t("common.showMore")}
            />
          </>
        ))
        .otherwise(() => (
          <LakeText color={colors.gray[300]}>{"—"}</LakeText>
        ))}

      <Popover
        referenceRef={buttonRef}
        visible={layer.isSome()}
        underlay={true}
        onDismiss={() => setLayer(Option.None())}
      >
        {layer
          .map(result =>
            match(result)
              .with({ __typename: "VerifyBeneficiaryNoMatch" }, () => (
                <LakeAlert
                  variant="error"
                  title={
                    <LakeText variant="semibold" color="inherit">
                      {t("transfer.bulk.beneficiaryVerification.noMatch")}
                    </LakeText>
                  }
                  subtitle={t("transfer.bulk.beneficiaryVerification.noMatch.description")}
                />
              ))
              .with({ __typename: "VerifyBeneficiaryCloseMatch" }, ({ nameSuggestion }) => (
                <LakeAlert
                  variant="info"
                  title={
                    <LakeText variant="semibold" color="inherit">
                      {t("transfer.bulk.beneficiaryVerification.closeMatch.alert", {
                        nameSuggestion,
                      })}
                    </LakeText>
                  }
                  subtitle={t("transfer.bulk.beneficiaryVerification.noMatch.description")}
                />
              ))
              .with({ __typename: "VerifyBeneficiaryNotPossible" }, () => (
                <LakeAlert
                  variant="warning"
                  title={
                    <LakeText variant="semibold" color="inherit">
                      {t("transfer.bulk.beneficiaryVerification.notPossible.alert")}
                    </LakeText>
                  }
                  subtitle={t(
                    "transfer.bulk.beneficiaryVerification.notPossible.alert.description",
                  )}
                />
              ))
              .otherwise(() => null),
          )
          .toNull()}
      </Popover>
    </Cell>
  );
};

const stickedToEndColumn: ColumnConfig<
  CreditTransferInput & {
    beneficiaryVerification?: VerifyBeneficiarySuccessPayloadFragment;
  },
  ExtraInfo
>[] = [
  {
    id: "verification",
    title: t("transfer.bulk.beneficiaryVerification"),
    width: 220,
    renderTitle: ({ title }) => <HeaderCell text={title} align="right" />,
    renderCell: ({ item }) => {
      return <VerificationCell beneficiaryVerification={item.beneficiaryVerification} />;
    },
  },
  {
    id: "amount",
    title: t("transfer.bulk.amount"),
    width: 150,
    renderTitle: ({ title }) => <HeaderCell text={title} align="right" />,
    renderCell: ({ item }) => {
      const amount = item.amount;
      return (
        <TextCell
          variant="medium"
          align="right"
          color={colors.gray[900]}
          text={formatCurrency(Number(amount.value), amount.currency)}
        />
      );
    },
  },
];

export const TransferBulkReview = ({
  creditTransferInputs,
  initialSelectedCreditTransferInputs,
  onPressPrevious,
  onSave,
}: Props) => {
  const [selected, setSelected] = useState(() => {
    if (initialSelectedCreditTransferInputs != null) {
      return new Set(
        initialSelectedCreditTransferInputs.map(item => creditTransferInputs.indexOf(item)),
      );
    } else {
      return new Set(creditTransferInputs.map((_item, index) => index));
    }
  });

  const onPressSubmit = () => {
    onSave(creditTransferInputs.filter((_item, index) => selected.has(index)));
  };

  return (
    <>
      <Tile flexGrow={1}>
        <Box direction="row" alignItems="center">
          <LakeText variant="smallMedium" color={colors.current.primary}>
            {t("transfer.bulk.allTransfers", { count: selected.size })}
          </LakeText>

          <Space width={12} />

          <Tag color="current" label={t("transfer.bulk.allTransfers.selected")}>
            {selected.size}
          </Tag>
        </Box>

        <Space height={8} />

        <View style={styles.scrollViewContainer}>
          <VirtualizedList
            variant="accented"
            rowHeight={56}
            headerHeight={56}
            data={creditTransferInputs}
            keyExtractor={(_item, index) => String(index)}
            extraInfo={{ selected, setSelected, totalCount: creditTransferInputs.length }}
            stickedToStartColumns={stickedToStartColumns}
            columns={columns}
            stickedToEndColumns={stickedToEndColumn}
            renderEmptyList={() => <EmptyView icon="error-circle-regular" borderedIcon={true} />}
          />
        </View>

        <Space height={32} />

        <LakeText color={colors.gray[700]}>
          {formatNestedMessage("transfer.bulk.youSendExactly", {
            amount: (
              <LakeText color={colors.current.primary} variant="semibold">
                {formatCurrency(
                  creditTransferInputs.reduce(
                    (acc, item, index) =>
                      acc + (selected.has(index) ? Number(item.amount.value) : 0),
                    0,
                  ),
                  "EUR",
                )}
              </LakeText>
            ),
          })}
        </LakeText>
      </Tile>

      <ResponsiveContainer breakpoint={800}>
        {({ small }) => (
          <LakeButtonGroup>
            <LakeButton mode="secondary" onPress={onPressPrevious} grow={small}>
              {t("common.previous")}
            </LakeButton>

            <LakeButton
              color="current"
              disabled={selected.size === 0}
              onPress={onPressSubmit}
              grow={small}
            >
              {t("common.continue")}
            </LakeButton>
          </LakeButtonGroup>
        )}
      </ResponsiveContainer>
    </>
  );
};
